import { useEditor } from "@craftjs/core";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { Recording } from "src/types";
import type { VideoJsPlayerOptions } from "video.js";

import { PlayerOverlay, RecordingPlayer } from "@/components/video-player";
import togglePageScrollLock from "@/utils/toggle-page-scroll-lock";

import PlayIcon from "./assets/playIcon";
import { Caption, Container, Duration, PlayButton, Thumbnail } from "./styles";

type VideoCardProps = {
  showCaption: boolean;
  recording: Recording;
  aspectRatio?: "square" | "wide";
  playerOptions?: VideoJsPlayerOptions;
  displayTitle?: string | null;
  displayThumbnailUrl?: string | null;
};

export const RecordingVideoCard = (props: VideoCardProps) => {
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const {
    showCaption,
    recording,
    aspectRatio = "square",
    playerOptions = {},
    displayTitle: displayTitleProp,
    displayThumbnailUrl,
  } = props;

  const displayTitle = (displayTitleProp ?? recording.title) || "";
  const thumbSrc = displayThumbnailUrl || recording.thumbnail;

  const { isEnabled } = useEditor((state) => ({
    isEnabled: state.isEnabled(),
  }));

  useEffect(
    function preventScrollOnPlay() {
      togglePageScrollLock({
        method: isPlayerOpen ? "add" : "remove",
        withOffset: false,
      });
    },
    [isPlayerOpen]
  );

  return (
    <>
      <Container>
        <Thumbnail
          $aspectRatio={aspectRatio}
          alt={displayTitle}
          src={thumbSrc}
        />
        <DurationTag duration={recording.duration} />
        {showCaption && <Caption>{displayTitle}</Caption>}
        <PlayButton onClick={() => setIsPlayerOpen(true)} disabled={isEnabled}>
          <span className="sr-only">Play video</span>
          {!isPlayerOpen && <PlayIcon />}
        </PlayButton>
      </Container>
      {isPlayerOpen &&
        createPortal(
          <PlayerOverlay
            title={displayTitle}
            onClose={() => setIsPlayerOpen(false)}
          >
            <RecordingPlayer
              recordingId={recording.id}
              src={recording.playableUrl}
              {...playerOptions}
            />
          </PlayerOverlay>,
          document.body
        )}
    </>
  );
};

export const DurationTag = ({ duration }: { duration: number }) => {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;

  const hourText = hours > 0 ? `${hours}:` : "";
  const minutesText = minutes < 10 ? `0${minutes}` : minutes;
  const secondsText = seconds < 10 ? `0${seconds}` : seconds;

  return <Duration>{`${hourText}${minutesText}:${secondsText}`}</Duration>;
};
